import { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { ImportOptions } from "./ImportOptions";

const LABELS = { replaced: "Updated", unchanged: "Already up to date", skipped: "Skipped", new: "Added",  drafted: "Unpublished for review", missing_keep: "Keep Questioning links skipped", strongs_errors: "Word-study lookups failed" };

function Summary({ p }) {
  if (p.error) return <p className="text-sm text-destructive" data-testid="sheet-error">Import failed: {p.error}</p>;
  if (!p.finished) return null;
  return (
    <div className="space-y-2 rounded-xl bg-muted/50 p-4 text-sm" data-testid="sheet-summary">
      <p className="font-semibold text-foreground">{p.dry ? "Test run (nothing changed)" : "Import finished"}: {p.file}</p>
      <ul className="grid gap-1 sm:grid-cols-2">
        {Object.entries(p.summary || {}).map(([k, n]) => (
          <li key={k} data-testid={`sheet-summary-${k}`}><span className="font-mono">{n}</span> {LABELS[k] || k}</li>
        ))}
      </ul>
      {p.mode === "update" && <FieldChanges p={p} />}
      {["replaced", "new", "skipped", "drafted", "missing_keep", "strongs_errors"].filter((k) => p.details?.[k]?.length).map((k) => (
        <details key={k}>
          <summary className="cursor-pointer text-muted-foreground">{LABELS[k]} ({p.details[k].length})</summary>
          <ul className="mt-1 max-h-48 overflow-auto font-mono text-xs text-muted-foreground">{p.details[k].map((l) => <li key={l}>{l}</li>)}</ul>
        </details>
      ))}
    </div>
  );
}

function FieldChanges({ p }) {
  const rows = Object.entries(p.fields || {});
  if (!rows.length) return null;
  return (
    <div className="space-y-2" data-testid="sheet-field-changes">
      <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Changes by field</p>
      {rows.map(([f, n]) => (
        <details key={f} data-testid={`sheet-field-change-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <summary className="cursor-pointer"><span className="font-mono">{n}</span> {f}</summary>
          <ul className="mt-1 space-y-1 text-xs text-muted-foreground">
            {(p.samples?.[f] || []).map((s) => (
              <li key={s.ref}><span className="font-mono text-foreground">{s.ref}</span>: <span className="line-through">{s.before || "(blank)"}</span> → {s.after || "(blank)"}</li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

const DEFAULT_OPTS = { mode: "replace", fields: [], unpublish_missing: true, create_new: true, blank_clears: false };

function confirmText(o, name) {
  if (o.mode === "update") return `Update ${o.fields.length} field(s) from ${name}?${o.unpublish_missing ? " Questions in these books that are not in the file will be unpublished." : ""}${o.create_new ? " New questions will be added as drafts." : ""}`;
  return `Import ${name}? Matching questions are replaced and published.${o.unpublish_missing ? " Questions in these books that are not in the file are unpublished." : ""}`;
}

export function SpreadsheetImport() {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [opts, setOpts] = useState(DEFAULT_OPTS);
  const [updatable, setUpdatable] = useState([]);
  const { data: p = {} } = useQuery({
    queryKey: ["sheet-status"], queryFn: async () => (await api.get("/admin/import/spreadsheet/status")).data,
    refetchInterval: (q) => (q.state.data?.running ? 3000 : false),
  });

  const start = async (dry) => {
    if (!dry && !window.confirm(confirmText(opts, file.name))) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("dry_run", dry ? "true" : "false");
      fd.append("mode", opts.mode);
      fd.append("fields", JSON.stringify(opts.fields));
      ["unpublish_missing", "create_new", "blank_clears"].forEach((k) => fd.append(k, opts[k] ? "true" : "false"));
      await api.post("/admin/import/spreadsheet", fd);
      toast.success(dry ? "Test run started." : "Import started.");
      await qc.invalidateQueries({ queryKey: ["sheet-status"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const pick = async (f) => {
    setFile(f);
    setUpdatable([]);
    setOpts((o) => ({ ...o, fields: [] }));
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      setUpdatable((await api.post("/admin/import/spreadsheet/columns", fd)).data.updatable);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  const blocked = !file || busy || p.running || (opts.mode === "update" && !opts.fields.length);

  return (
    <section className="space-y-4 rounded-2xl border border-border bg-card p-5" data-testid="sheet-import">
      <div>
        <h2 className="font-serif text-2xl font-semibold text-foreground">Upload spreadsheet</h2>
        <p className="mt-1 text-sm text-muted-foreground">A QuestionGod spec workbook (.xlsx with a “Questions” sheet). Rows are matched to questions by Verse (and Question text when a verse has several). Choose a file, pick how it should apply, then run a test first to see what would change.</p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid="sheet-file-input" />
        <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} data-testid="sheet-choose"><FileSpreadsheet className="mr-2 h-4 w-4" />{file ? file.name : "Choose .xlsx"}</Button>
        <Button variant="outline" className="rounded-full" disabled={blocked} onClick={() => start(true)} data-testid="sheet-dry-run">Test run</Button>
        <Button className="rounded-full bg-primary text-primary-foreground" disabled={blocked} onClick={() => start(false)} data-testid="sheet-import-run">Import</Button>
      </div>
      {file && <ImportOptions opts={opts} setOpts={setOpts} updatable={updatable} />}
      {p.running && (
        <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="sheet-progress">
          <Loader2 className="h-4 w-4 animate-spin" /> {p.dry ? "Testing" : "Importing"} {p.file}… {p.total ? `${p.done} / ${p.total}` : "reading file"}
        </p>
      )}
      {!p.running && (!file || p.file === file.name) && <Summary p={p} />}
    </section>
  );
}
